import React, { useState, useEffect } from 'react';
import { api, apiClient } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import StatsCard from '@/components/dashboard/StatsCard';
import { 
  Building2,
  Users,
  Heart,
  FileText,
  ArrowRight,
  DollarSign,
  CheckCircle2,
  Clock,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    employers: 0,
    employees: 0,
    activeEnrollments: 0,
    pendingEnrollments: 0,
    products: 0,
    totalPremiums: 0
  });
  const [recentEnrollments, setRecentEnrollments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrationRequired, setMigrationRequired] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employers, employeesData, enrollments, productsData, migrationStatus] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.Employee.filter({ is_active: true }),
        api.entities.Enrollment.list(),
        api.entities.BenefitProduct.list(),
        apiClient.get('/public/migrations-status')
      ]);

      const activeEnrollments = enrollments.filter(e => e.status === 'active');
      const pendingEnrollments = enrollments.filter(e => e.status === 'pending');
      const totalPremiums = activeEnrollments.reduce((sum, e) => sum + (e.premium_amount || 0), 0);

      setStats({
        employers: employers.length,
        employees: employeesData.length,
        activeEnrollments: activeEnrollments.length,
        pendingEnrollments: pendingEnrollments.length,
        products: productsData.length,
        totalPremiums
      });

      setRecentEnrollments(enrollments.slice(0, 5));
      setEmployees(employeesData);
      setProducts(productsData);
      setMigrationRequired(!!migrationStatus?.data?.requires_migration);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown';
  };

  // Chart data
  const enrollmentData = [
    { month: 'Jan', enrollments: 45 },
    { month: 'Feb', enrollments: 52 },
    { month: 'Mar', enrollments: 48 },
    { month: 'Apr', enrollments: 61 },
    { month: 'May', enrollments: 55 },
    { month: 'Jun', enrollments: 67 },
  ];

  const categoryData = [
    { name: 'Health', value: 35, color: '#10b981' },
    { name: 'Dental', value: 25, color: '#3b82f6' },
    { name: 'Vision', value: 20, color: '#8b5cf6' },
    { name: 'Life', value: 15, color: '#f59e0b' },
    { name: 'Other', value: 5, color: '#6b7280' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Overview of your benefits enrollment system
          </p>
          {migrationRequired && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Pending database migrations detected. Please run migrations from the admin tools.
              <Link to={createPageUrl('AdminSettings')} className="font-medium underline">Go to Admin Settings</Link>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <Link to={createPageUrl('CensusUpload')}>
            <Button variant="outline">
              Upload Census
            </Button>
          </Link>
          <Link to={createPageUrl('EnrollmentReports')}>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              View Reports
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatsCard
            title="Total Employers"
            value={stats.employers}
            subtitle="Active companies"
            icon={Building2}
            gradient="from-blue-500 to-indigo-500"
            trendValue="+12%"
            trend="up"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatsCard
            title="Total Employees"
            value={stats.employees}
            subtitle="Active employees"
            icon={Users}
            gradient="from-emerald-500 to-teal-500"
            trendValue="+8%"
            trend="up"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatsCard
            title="Active Enrollments"
            value={stats.activeEnrollments}
            subtitle={`${stats.pendingEnrollments} pending`}
            icon={Heart}
            gradient="from-purple-500 to-pink-500"
          />
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatsCard
            title="Monthly Premiums"
            value={`$${stats.totalPremiums.toLocaleString()}`}
            subtitle="Total collected"
            icon={DollarSign}
            gradient="from-amber-500 to-orange-500"
            trendValue="+15%"
            trend="up"
          />
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Enrollment Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-500" />
                Enrollment Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={enrollmentData}>
                  <defs>
                    <linearGradient id="colorEnrollments" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="enrollments" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fill="url(#colorEnrollments)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">By Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {categoryData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-slate-600">{item.name}</span>
                    <span className="font-medium ml-auto">{item.value}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Recent Enrollments
            </CardTitle>
            <Link to={createPageUrl('EnrollmentReports')}>
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentEnrollments.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No recent enrollments</p>
              ) : (
                recentEnrollments.map((enrollment) => (
                  <div 
                    key={enrollment.id}
                    className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-medium">
                        {getEmployeeName(enrollment.employee_id).charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {getEmployeeName(enrollment.employee_id)}
                        </p>
                        <p className="text-sm text-slate-500">
                          {getProductName(enrollment.product_id)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">
                        ${enrollment.premium_amount?.toFixed(2)}/mo
                      </p>
                      <Badge className={
                        enrollment.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }>
                        {enrollment.status === 'active' ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
                        ) : (
                          <><Clock className="w-3 h-3 mr-1" /> Pending</>
                        )}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to={createPageUrl('Employers')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Manage Employers</p>
                <p className="text-sm text-slate-500">View & edit companies</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to={createPageUrl('EmployeeManagement')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Manage Employees</p>
                <p className="text-sm text-slate-500">View & edit profiles</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to={createPageUrl('BenefitsManagement')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Heart className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Manage Benefits</p>
                <p className="text-sm text-slate-500">Products & categories</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to={createPageUrl('AuditLog')} className="block">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Audit Log</p>
                <p className="text-sm text-slate-500">Track all changes</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}